import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  ArrowRight,
  ArrowRightLeft,
  Boxes,
  CheckCircle2,
  CloudUpload,
  FileDown,
  KeyRound,
  Search,
  Server,
  UserCheck,
} from 'lucide-react';

const IDTA_BLUE = '#0028CD';

const providerSteps = [
  {
    num: '01',
    icon: UserCheck,
    title: 'Onboard',
    desc: 'Your company infrastructure is verified and onboarded into the ADX Hub trust list through the Identity Provider.',
  },
  {
    num: '02',
    icon: CloudUpload,
    title: 'Register AAS servers',
    desc: 'Register your AAS server endpoints and company information in the Discovery service so authorized consumers can find your endpoints and AAS.',
  },
  {
    num: '03',
    icon: FileDown,
    title: 'Fetch Submodel templates',
    desc: 'Pull the latest IDTA Submodel templates from the SMT Repository via API and use them to model your AAS.',
  },
  {
    num: '04',
    icon: CheckCircle2,
    title: 'Validate',
    desc: 'Use the Validation service to check AAS files, Submodels, and your AAS server APIs against IEC 63278 and IDTA specifications.',
  },
];

const consumerSteps = [
  {
    num: '01',
    icon: UserCheck,
    title: 'Onboard',
    desc: 'Sign in through the Identity Provider. Your organization is verified and added to the ADX Hub trust list.',
  },
  {
    num: '02',
    icon: Search,
    title: 'Discover',
    desc: 'Search for partners and their AAS endpoints by company using the Discovery service, then explore their AAS using the built-in client inside Discovery.',
  },
  {
    num: '03',
    icon: KeyRound,
    title: 'Request with token',
    desc: 'Your AAS client calls the provider\u2019s AAS server with a signed token. The provider verifies it against the trust list hosted at ADX Hub.',
  },
  {
    num: '04',
    icon: Boxes,
    title: 'Receive AAS',
    desc: 'The provider\u2019s AAS server returns the requested AAS, Submodel, or SubmodelElement directly. Data never passes through ADX Hub.',
  },
];

const tabs = [
  { key: 'providers', label: 'For data providers', icon: Server, steps: providerSteps },
  { key: 'consumers', label: 'For data consumers', icon: ArrowRightLeft, steps: consumerSteps },
];

function StepCard({ step, index, isSelected, onSelect }) {
  const Icon = step.icon;
  return (
    <motion.button
      type="button"
      onClick={() => onSelect(index)}
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 0.35, delay: index * 0.08 }}
      whileHover={{ y: -4 }}
      whileTap={{ scale: 0.98 }}
      className={`flex min-w-0 flex-1 flex-col items-center text-center transition-colors duration-200 ${
        isSelected ? 'cursor-default' : 'cursor-pointer'
      }`}
      data-testid={`step-${step.num}`}
    >
      <motion.div
        className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-full border transition-colors duration-200 md:h-16 md:w-16 ${
          isSelected
            ? 'bg-[#0028CD] text-white border-[#0028CD] shadow-[0_8px_24px_-8px_rgba(0,40,205,0.45)]'
            : 'bg-[#0028CD]/5 border-[#0028CD]/20 text-[#0028CD] hover:bg-[#0028CD]/10'
        }`}
        animate={isSelected ? { scale: 1.04 } : { scale: 1 }}
      >
        <Icon className="h-6 w-6 md:h-7 md:w-7" strokeWidth={1.5} />
      </motion.div>
      <span
        className="mt-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-400 md:mt-4 md:tracking-[0.24em]"
      >
        Step {step.num}
      </span>
      <h3
        className={`mt-1 min-h-[2.6rem] text-[14px] font-semibold leading-tight tracking-tight transition-colors duration-200 md:mt-1.5 md:min-h-[3rem] md:text-[15px] ${
          isSelected ? 'text-[#0028CD]' : 'text-[#0f172a]'
        }`}
      >
        {step.title}
      </h3>
      <p className="mt-1.5 max-w-[280px] text-[13px] leading-[1.55] text-slate-600 md:mt-2 md:max-w-[260px] md:text-[13.5px] md:leading-[1.6]">
        {step.desc}
      </p>
    </motion.button>
  );
}

function Connector() {
  return (
    <div className="hidden shrink-0 items-center px-3 md:flex md:flex-1 md:max-w-[100px] lg:max-w-[140px]">
      <div className="h-px flex-1" style={{ backgroundColor: 'rgba(0,40,205,0.18)' }} />
      <ArrowRight className="ml-1 h-4 w-4 shrink-0" style={{ color: IDTA_BLUE }} strokeWidth={2} />
    </div>
  );
}

export default function HowItWorks() {
  const [activeTab, setActiveTab] = useState(0);
  const [selectedStep, setSelectedStep] = useState(0);
  const current = tabs[activeTab];
  const steps = current.steps;

  const handleTabChange = (i) => {
    setActiveTab(i);
    setSelectedStep(0);
  };

  return (
    <section
      id="how-it-works"
      className="bg-white py-14 md:py-20"
      data-testid="how-it-works-section"
    >
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.25 }}
          transition={{ duration: 0.4 }}
          className="text-center"
        >
          <h2 className="text-[2rem] font-bold tracking-tight text-[#0028CD] md:text-4xl">
            How it works
          </h2>
          <p className="mx-auto mt-4 max-w-3xl text-[15px] leading-relaxed text-slate-500">
            ADX Hub provides shared services such as Identity, Trust List, Discovery, and Validation for data exchange. AAS data is exchanged directly between data providers and data consumers, with full control over the data.
          </p>
        </motion.div>

        <div className="mt-8 flex justify-center md:mt-10">
          <div className="inline-flex w-full max-w-[22rem] gap-1 rounded-full border border-slate-200 bg-slate-50 p-1 md:w-auto md:max-w-none">
            {tabs.map((tab, i) => {
              const TabIcon = tab.icon;
              const isActive = i === activeTab;
              return (
                <motion.button
                  key={tab.key}
                  type="button"
                  onClick={() => handleTabChange(i)}
                  data-testid={`tab-${tab.key}`}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className={`inline-flex flex-1 items-center justify-center gap-1.5 rounded-full px-3 py-2 text-[12px] font-medium transition-all duration-200 md:flex-none md:gap-2 md:px-5 md:py-2.5 md:text-[13px] ${
                    isActive
                      ? 'text-white'
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                  style={isActive ? { backgroundColor: IDTA_BLUE } : {}}
                >
                  <TabIcon className="h-3.5 w-3.5 md:h-4 md:w-4" strokeWidth={1.6} />
                  {tab.label}
                </motion.button>
              );
            })}
          </div>
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.25 }}
            className="mt-9 md:mt-12"
          >
            <div className="flex flex-col gap-7 md:flex-row md:items-start md:justify-center md:gap-0">
              {steps.map((step, i) => (
                <React.Fragment key={step.num}>
                  <StepCard
                    step={step}
                    index={i}
                    isSelected={selectedStep === i}
                    onSelect={setSelectedStep}
                  />
                  {i < steps.length - 1 && <Connector />}
                </React.Fragment>
              ))}
            </div>

            <div className="mt-8 flex justify-center gap-2 md:mt-10">
              {steps.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setSelectedStep(i)}
                  aria-label={`Go to step ${i + 1}`}
                  className={`h-2 rounded-full transition-all duration-200 ${
                    selectedStep === i ? 'w-6 bg-[#0028CD]' : 'w-2 bg-slate-200 hover:bg-slate-300'
                  }`}
                />
              ))}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
}
